(function () {
  const SWIPER_SRC = 'https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.js';

  // Every add-to-cart in the theme lands on this event (see assets/events.js).
  const CART_UPDATE_EVENT = 'cart:update';

  /** @type {Promise<Set<string>> | null} */
  let cartProductIdsRequest = null;

  /**
   * Product ids currently in the cart. The event carries only the id of the
   * product that triggered it, so the cart itself is the authority -- and it
   * covers items added from anywhere else on the page too.
   *
   * Concurrent callers (several bumps on one page) share a single request.
   *
   * @returns {Promise<Set<string>>}
   */
  function fetchCartProductIds() {
    if (cartProductIdsRequest) return cartProductIdsRequest;

    const cartUrl = window.Theme?.routes?.cart_url || '/cart';

    cartProductIdsRequest = fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => new Set((cart.items || []).map((item) => String(item.product_id))))
      .catch(() => new Set())
      .finally(() => {
        cartProductIdsRequest = null;
      });

    return cartProductIdsRequest;
  }

  /**
   * Loads Swiper once per page, sharing the promise with the other sections
   * that rely on it (see tp-video-slides.js).
   * @returns {Promise<void>}
   */
  function loadSwiper() {
    if (window.Swiper) return Promise.resolve();
    if (window.tpSwiperLoadPromise) return window.tpSwiperLoadPromise;

    window.tpSwiperLoadPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[data-tp-swiper]');

      if (existingScript) {
        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = SWIPER_SRC;
      script.defer = true;
      script.dataset.tpSwiper = 'true';
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    return window.tpSwiperLoadPromise;
  }

  if (!customElements.get('order-bump-swiper')) {
    class OrderBumpSwiper extends HTMLElement {
      /** @type {MutationObserver | null} */
      #observer = null;
      /** @type {ResizeObserver | null} */
      #resizeObserver = null;
      #initializing = false;

      connectedCallback() {
        this.swiper = null;
        this.initializeSwiper();
        document.addEventListener(CART_UPDATE_EVENT, this.#handleCartUpdate);
      }

      disconnectedCallback() {
        document.removeEventListener(CART_UPDATE_EVENT, this.#handleCartUpdate);
        this.#observer?.disconnect();
        this.#observer = null;
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = null;
        this.destroySwiper();
      }

      /**
       * Drops the slides for products that just entered the cart.
       *
       * The cart drawer gets this for free -- it re-renders server-side, and the
       * snippet already filters the cart out. On a product page nothing
       * re-renders, so the slide would otherwise sit there offering an item the
       * customer already has.
       *
       * Removal is one-way: emptying the cart again does not bring the slide
       * back until the next page load, which re-renders the full list anyway.
       */
      #handleCartUpdate = async () => {
        if (!this.isConnected) return;
        if (!this.querySelector('[data-order-bump-product-id]')) return;

        const cartProductIds = await fetchCartProductIds();

        if (!this.isConnected || cartProductIds.size === 0) return;

        let removedAny = false;

        this.querySelectorAll('[data-order-bump-product-id]').forEach((slide) => {
          if (!(slide instanceof HTMLElement)) return;
          if (!cartProductIds.has(String(slide.dataset.orderBumpProductId))) return;

          slide.remove();
          removedAny = true;
        });

        if (!removedAny) return;

        // Nothing left to suggest: hide the title and slider rather than leave
        // an empty box behind.
        if (!this.querySelector('[data-order-bump-product-id]')) {
          const wrapper = this.closest('.cart-drawer__order-bump, .order-bump');
          if (wrapper instanceof HTMLElement) wrapper.hidden = true;
          this.destroySwiper();
          return;
        }

        this.swiper?.update();
      };

      /**
       * The drawer lives in a `<dialog>`, so on the first render the container
       * is display:none and every slide measures 0px wide. Recalculate as soon
       * as it actually has a width, otherwise the slider scrolls to slides that
       * were laid out with no size.
       *
       * @param {Element} swiperElement - The Swiper container.
       */
      #updateOnFirstLayout(swiperElement) {
        this.#resizeObserver?.disconnect();

        if (!(swiperElement instanceof HTMLElement) || swiperElement.offsetWidth > 0) return;

        this.#resizeObserver = new ResizeObserver(() => {
          if (swiperElement.offsetWidth === 0) return;

          this.#resizeObserver?.disconnect();
          this.#resizeObserver = null;
          this.swiper?.update();
        });

        this.#resizeObserver.observe(swiperElement);
      }

      /**
       * The cart drawer re-renders through `morphSection`, which diffs the live
       * DOM against freshly rendered server HTML. Everything Swiper adds -- the
       * `swiper-initialized` classes, the inline transform on the wrapper, the
       * pagination bullets -- is absent from that HTML, so the morph strips it
       * and leaves a dead slider behind.
       *
       * Watching the container's own class attribute catches that no matter
       * what triggered the re-render, including the async section-render path
       * where the DOM is rewritten well after the cart event fired.
       *
       * @param {Element} swiperElement - The Swiper container.
       */
      #watchForMorph(swiperElement) {
        this.#observer?.disconnect();

        this.#observer = new MutationObserver(() => {
          if (this.#initializing || !this.isConnected) return;
          if (swiperElement.classList.contains('swiper-initialized')) return;

          this.initializeSwiper();
        });

        // Only the container's class list: Swiper leaves it alone while
        // sliding (it retags the slides instead), so this stays quiet.
        this.#observer.observe(swiperElement, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      destroySwiper() {
        if (!this.swiper) return;

        try {
          // Detaches Swiper's listeners; the DOM it had mutated may already
          // have been rewritten by the morph, so failures here are expected.
          this.swiper.destroy(true, true);
        } catch (error) {
          // Ignored on purpose: the instance is discarded either way.
        }

        this.swiper = null;
      }

      async initializeSwiper() {
        this.#initializing = true;

        try {
          await loadSwiper();
        } catch (error) {
          this.#initializing = false;
          this.dataset.swiperError = 'true';
          return;
        }

        const swiperElement = this.querySelector('[data-order-bump-swiper]');

        if (!this.isConnected || !window.Swiper || !swiperElement) {
          this.#initializing = false;
          return;
        }

        this.destroySwiper();

        const paginationElement = this.querySelector('[data-order-bump-pagination]');
        const slideCount = this.querySelectorAll('.swiper-slide').length;

        try {
          this.swiper = new window.Swiper(swiperElement, {
            slidesPerView: 1,
            spaceBetween: 5,
            watchOverflow: true,
            threshold: 8,
            loop: false,
            a11y: { enabled: true },
            keyboard: { enabled: true, onlyInViewport: true },
            pagination:
              paginationElement && slideCount > 1
                ? {
                    el: paginationElement,
                    clickable: true,
                  }
                : false,
          });
        } finally {
          this.#initializing = false;
        }

        this.#watchForMorph(swiperElement);
        this.#updateOnFirstLayout(swiperElement);
      }
    }

    customElements.define('order-bump-swiper', OrderBumpSwiper);
  }
})();
